<script lang="ts">
  import { app } from "../../state/app.svelte";
  import { i18n } from "../../i18n/i18n.svelte";

  const fr = {
    close: "Fermer",
    title: "⚠️ Recherche web — confidentialité",
    dontShowAgain: "Ne plus afficher",
    understood: "J'ai compris",
  };
  const en: typeof fr = {
    close: "Close",
    title: "⚠️ Web search — privacy",
    dontShowAgain: "Don't show again",
    understood: "Got it",
  };
  const t = $derived(i18n.locale === "en" ? en : fr);

  function onKeydown(e: KeyboardEvent) {
    if (app.exaWebWarningOpen && e.key === "Escape") app.closeExaWebWarning(false);
  }
</script>

<svelte:window onkeydown={onKeydown} />

{#if app.exaWebWarningOpen}
  <div
    class="backdrop"
    onclick={() => app.closeExaWebWarning(false)}
    onkeydown={() => {}}
    role="button"
    tabindex="-1"
    aria-label={t.close}
  >
    <div
      class="modal"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="exa-warn-title"
      onclick={(e) => e.stopPropagation()}
      onkeydown={() => {}}
      tabindex="-1"
    >
      <h2 id="exa-warn-title">{t.title}</h2>
      {#if i18n.locale === "en"}
        <p>
          When web search is on, your queries (and excerpts of your question)
          are sent to an <strong>external search engine</strong> (Exa), outside
          EPFL's infrastructure.
        </p>
        <p>
          <strong>Do not use web search for confidential documents or documents
          containing personal data</strong>: sensitive information or personal
          data could leak into the calls made to the external service.
        </p>
      {:else}
        <p>
          En activant la recherche web, vos requêtes (et des extraits de votre
          question) sont envoyées à un <strong>moteur de recherche externe</strong>
          (Exa), hors de l'infrastructure EPFL.
        </p>
        <p>
          <strong>N'utilisez pas la recherche web pour des documents confidentiels
          ou contenant des données personnelles</strong> : des informations
          sensibles ou des données personnelles pourraient transparaître dans les
          appels au service externe.
        </p>
      {/if}
      <div class="actions">
        <button class="secondary" onclick={() => app.closeExaWebWarning(true)}>
          {t.dontShowAgain}
        </button>
        <button class="primary" onclick={() => app.closeExaWebWarning(false)}>
          {t.understood}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 110;
    padding: 1rem;
    animation: fade 0.12s ease-out;
  }

  .modal {
    background: var(--epfl-white);
    border-radius: var(--epfl-radius-lg);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
    max-width: 420px;
    width: 100%;
    padding: 1.1rem 1.2rem 1rem;
    animation: slide 0.15s ease-out;
  }

  h2 {
    margin: 0 0 0.6rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--epfl-warning-text);
  }

  p {
    margin: 0 0 0.6rem;
    font-size: 0.85rem;
    line-height: 1.45;
    color: var(--epfl-gray-700);
  }

  p strong {
    color: var(--epfl-text);
  }

  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 0.9rem;
  }

  button {
    padding: 0.45rem 0.9rem;
    border-radius: var(--epfl-radius);
    font-size: 0.82rem;
    font-family: inherit;
    cursor: pointer;
    border: 1px solid transparent;
  }

  .primary {
    background: var(--epfl-accent);
    color: var(--epfl-white);
  }

  .primary:hover {
    background: var(--epfl-accent-dark);
  }

  .secondary {
    background: var(--epfl-white);
    color: var(--epfl-gray-600);
    border-color: var(--epfl-gray-200);
  }

  .secondary:hover {
    background: var(--epfl-gray-50);
    color: var(--epfl-text);
  }

  @keyframes fade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slide {
    from {
      transform: translateY(12px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
</style>
